import { GetterTree, Module } from 'vuex'
import { RootState } from '@/@next/store'

import { mutations } from './mutations'

import * as APP from '@/@next/store/app/types'

import { NetworkStatus } from '@/@next/@types'
import { DataToPreload } from '@/@types/graphql'

export enum DataToPreloadStrategy {
  inherit = 'inherit',
  force = 'force',
  disable = 'disable',
  reactNativeWebView = 'reactNativeWebView'
}

export enum PreloadingDataStatus {
  unstarted = 'unstarted',
  loading = 'loading',
  error = 'error',
  complete = 'complete',
  uncomplete = 'uncomplete'
}

export enum PreloadConsentStatus {
  unknown = 'unknown',
  accepted = 'accepted',
  rejected = 'rejected'
}

export class AppState {
  network: NetworkStatus = {}
  modals: any[] = []
  isReady: boolean = false
  serviceWorkerIsReady: boolean = false
  // Preloader variables
  preloadIsReady: boolean = false
  preloadConfiguration: any = null
  dataToPreload: DataToPreload | null = null
  preloadRessources: any = {
    status: PreloadingDataStatus.unstarted,
    cached: []
  }
  preloadFiles: any = {
    status: PreloadingDataStatus.unstarted,
    cached: []
  }
  showPreloadModal: boolean = false
  concentPreload = PreloadConsentStatus.unknown
  preloadSuccess: boolean = false
  preloadError: boolean = false
  needToReloadApp: boolean = false
  showPreloadTopBarButton: boolean = false
  preloadStrategy: DataToPreloadStrategy = DataToPreloadStrategy.inherit
}

export const preloadConfigurationWithForceStrategy = {
  needConsent: false,
  showConsentTimeout: 2000,
  resources: {
    enable: true,
    needConsent: false,
    timeout: 5000,
    limit: null
  },
  files: {
    enable: true,
    needConsent: false,
    timeout: 10000, 
    limit: null
  }
}

export const preloadConfigurationWithReactNativeWebViewStrategy = {
  disableNotification: true,
  needConsent: false,
  showConsentTimeout: 0,
  resources: {
    enable: true,
    needConsent: false,
    timeout: 5000,
    limit: null
  },
  files: {
    enable: true,
    needConsent: false,
    timeout: 10000, 
    limit: null
  }
}

const state: AppState = new AppState()

const getters: GetterTree<AppState, RootState> = {
  [APP.APP_IS_READY]: (state) => state.isReady,
  [APP.GET_NETWORK]: (state) => state.network,
  [APP.GET_MODALS]: (state) => state.modals,
  [APP.SERVICE_WORKER_IS_READY]: (state) => state.serviceWorkerIsReady,
  // Preloader Getters
  [APP.SHOW_PRELOAD_TOP_BAR_BUTTON]: (state) => state.showPreloadTopBarButton,
  [APP.PRELOAD_CONFIGURATION]: (state) => state.preloadConfiguration || null,
  [APP.DATA_TO_PRELOAD]: (state) => state.dataToPreload,
  [APP.PRELOAD_IS_READY]: (state) => state.preloadIsReady,
  [APP.PRELOAD_RESSOURCES]: (state) => state.preloadRessources,
  [APP.PRELOAD_RESSOURCE_STATUS]: (state) => state.preloadRessources.status,
  [APP.PRELOAD_FILES]: (state) => state.preloadFiles,
  [APP.PRELOAD_FILE_STATUS]: (state) => state.preloadFiles.status,
  [APP.SHOW_PRELOAD_MODAL]: (state) => state.showPreloadModal,
  [APP.CONSENT_PRELOAD]: (state) => state.concentPreload,
  [APP.PRELOAD_LOADING]: (state) => {
    return state.preloadRessources.status === PreloadingDataStatus.loading
    || state.preloadFiles.status === PreloadingDataStatus.loading
  },
  [APP.PRELOAD_SUCCESS]: (state) => {
    return state.preloadRessources.status === PreloadingDataStatus.complete
    && state.preloadFiles.status === PreloadingDataStatus.complete
  },
  [APP.PRELOAD_ERROR]: (state) => {
    return state.preloadRessources.status === PreloadingDataStatus.error 
        || state.preloadRessources.status === PreloadingDataStatus.error
        || state.preloadFiles.status === PreloadingDataStatus.uncomplete 
        || state.preloadFiles.status === PreloadingDataStatus.uncomplete 
  },
  [APP.NEED_TO_RELOAD_APP]: (state) => state.needToReloadApp,
  [APP.DATA_TO_PRELOAD_STRATEGY]: (state) => state.preloadStrategy
}

export const app: Module<AppState, RootState> = {
  state,
  getters,
  mutations
}
